import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getCurrentConference } from '../../../base/conference/functions';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { getLocalParticipant } from '../../../base/participants/functions';
import {
    clearRemoteAnnotations,
    setAnnotationDrawing,
    setRemoteAnnotationAllowed,
    showLocalShareWithAnnotations
} from '../../actions';
import { ANNOTATION_MESSAGE, POINTER_MESSAGE, SEND_INTERVAL_MS } from '../../constants';
import {
    getAnnotationColor,
    getStageScreenShareOwnerId,
    getVideoContentRect,
    isRemoteAnnotationAllowed,
    sendAnnotationMessage
} from '../../functions';
import { addSegment, clearShare, getVisible, subscribe } from '../../strokes';

/**
 * Counter for stroke ids, unique per sender.
 */
let _strokeCount = 0;

/**
 * Rounds a normalized coordinate to what a message needs (0.4px at 4K).
 *
 * @param {number} value - Coordinate in 0..1.
 * @returns {number}
 */
function _round(value) {
    return Math.round(value * 10000) / 10000;
}

/**
 * The picture of the large video next to the overlay, in client coordinates.
 *
 * @param {HTMLCanvasElement} canvas - The overlay canvas.
 * @returns {Object|null}
 */
function _getFrameRect(canvas) {
    const video = canvas.parentElement?.querySelector('#largeVideo');

    return video ? getVideoContentRect(video) : null;
}

/**
 * Paints what was drawn on a screen share onto the overlay canvas.
 *
 * @param {HTMLCanvasElement} canvas - The overlay canvas.
 * @param {string} sharerId - The share on stage.
 * @param {string} localId - The local participant, whose pointer is not drawn.
 * @returns {boolean} Whether anything is still visible, i.e. fading.
 */
function _paint(canvas, sharerId, localId) {
    const host = canvas.getBoundingClientRect();
    const ratio = canvas.ownerDocument.defaultView.devicePixelRatio || 1;
    const width = Math.round(host.width * ratio);
    const height = Math.round(host.height * ratio);
    const context = canvas.getContext('2d');
    const { segments, pointers } = getVisible(sharerId);
    const rect = _getFrameRect(canvas);

    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, host.width, host.height);

    if (rect) {
        const toX = x => rect.left - host.left + (x * rect.width);
        const toY = y => rect.top - host.top + (y * rect.height);

        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.lineWidth = Math.max(2, rect.height / 240);
        segments.forEach(({ alpha, color, points }) => {
            context.globalAlpha = alpha;
            context.strokeStyle = color;
            context.beginPath();
            context.moveTo(toX(points[0][0]), toY(points[0][1]));
            points.forEach(([ x, y ]) => context.lineTo(toX(x), toY(y)));
            context.stroke();
        });
        context.globalAlpha = 1;

        context.font = '600 12px sans-serif';
        context.textBaseline = 'middle';
        pointers.forEach(({ alpha, color, name, senderId, x, y }) => {
            if (senderId === localId) {
                return;
            }

            const px = toX(x);
            const py = toY(y);

            context.globalAlpha = alpha;
            context.fillStyle = color;
            context.strokeStyle = '#fff';
            context.lineWidth = 2;
            context.beginPath();
            context.arc(px, py, 6, 0, 2 * Math.PI);
            context.fill();
            context.stroke();
            if (name) {
                context.fillStyle = 'rgba(0, 0, 0, 0.7)';
                context.fillRect(px + 10, py - 9, context.measureText(name).width + 12, 18);
                context.fillStyle = '#fff';
                context.fillText(name, px + 16, py);
            }
        });
        context.globalAlpha = 1;
    }

    return segments.length > 0 || pointers.length > 0;
}

const _canvasStyle = {
    height: '100%',
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: '100%',

    // Over the video and the local-share placeholder; click-through.
    zIndex: 10
};

const _buttonStyle = {
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 13,
    fontWeight: 600,
    gap: 6,
    padding: '6px 10px'
};

const _styles = {
    activeButton: {
        ..._buttonStyle,
        background: 'rgba(255, 255, 255, 0.2)'
    },
    bar: {
        background: 'rgba(0, 0, 0, 0.6)',
        borderRadius: 8,
        display: 'flex',
        gap: 4,
        left: '50%',
        padding: 4,
        position: 'absolute',
        top: 12,
        transform: 'translateX(-50%)',
        zIndex: 1004
    },
    button: _buttonStyle,
    canvas: _canvasStyle,

    // Takes the pointer from everything on stage, the toolbox included, until
    // the viewer is done (button or Escape).
    drawingCanvas: {
        ..._canvasStyle,
        cursor: 'crosshair',
        pointerEvents: 'auto',
        touchAction: 'none',
        zIndex: 1003
    },
    swatch: {
        borderRadius: '50%',
        height: 10,
        width: 10
    }
};

/**
 * Drawing layer over the large video while it shows a screen share. Viewers
 * draw here; everyone sees what viewers draw, live and fading. The sharer
 * gets the switch for viewer drawing and a way to clear it.
 *
 * @returns {ReactElement|null}
 */
export default function ScreenAnnotationOverlay() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const sharerId = useSelector(getStageScreenShareOwnerId);
    const localParticipant = useSelector(getLocalParticipant);
    const allowed = useSelector(state => Boolean(sharerId) && isRemoteAnnotationAllowed(state, sharerId));
    const drawing = useSelector(state => state['features/screen-annotation'].drawing);
    const conference = useSelector(getCurrentConference);
    const inPip = useSelector(getPictureInPictureModeConfig);
    const canvasRef = useRef(null);
    const localId = localParticipant?.id;
    const localName = localParticipant?.name;
    const isSharer = Boolean(sharerId) && sharerId === localId;
    const canDraw = Boolean(sharerId) && !isSharer && allowed;
    const active = drawing && canDraw;
    const color = useSelector(state => getAnnotationColor(state, localId));

    // Nothing left to draw on: the share ended or moved, or the sharer turned
    // viewer drawing off.
    useEffect(() => {
        drawing && !canDraw && dispatch(setAnnotationDrawing(false));
    }, [ canDraw, drawing ]);

    // Strokes belong to the share they were drawn on.
    useEffect(() => () => clearShare(), [ sharerId ]);

    useEffect(() => {
        const canvas = canvasRef.current;

        if (!canvas) {
            return undefined;
        }

        let frame;
        let frameWindow;

        // Paint while anything is visible. The canvas moves into the Document
        // PiP window with the rest of the meeting, so this asks that window
        // for frames: the meeting tab's are paused while it is hidden.
        const schedule = () => {
            if (frame) {
                return;
            }
            frameWindow = canvas.ownerDocument.defaultView;
            frame = frameWindow.requestAnimationFrame(() => {
                frame = undefined;
                _paint(canvas, sharerId, localId) && schedule();
            });
        };
        const unsubscribe = subscribe(schedule);

        schedule();

        return () => {
            unsubscribe();
            frame && frameWindow.cancelAnimationFrame(frame);
        };
    }, [ localId, sharerId ]);

    useEffect(() => {
        const canvas = canvasRef.current;

        if (!active || !canvas) {
            return undefined;
        }

        const keyWindow = canvas.ownerDocument.defaultView;
        let stroke;
        let last;
        let timer;
        let lastPointerAt = 0;

        const send = (type, payload) => sendAnnotationMessage(conference, type, {
            ...payload,
            participantId: localId,
            sharerId
        });
        const toFrame = event => {
            const rect = _getFrameRect(canvas);
            const x = rect && (event.clientX - rect.left) / rect.width;
            const y = rect && (event.clientY - rect.top) / rect.height;

            return rect && x >= 0 && x <= 1 && y >= 0 && y <= 1 ? [ _round(x), _round(y) ] : null;
        };

        // Sends what was drawn since the last message. It repeats the last
        // point already sent, so the pieces join up on the other side - and a
        // tap still has the two points a stroke needs.
        const flush = () => {
            clearTimeout(timer);
            timer = undefined;
            if (!stroke || stroke.points.length <= stroke.sent) {
                return;
            }

            const points = stroke.points.slice(Math.max(0, stroke.sent - 1));

            stroke.sent = stroke.points.length;
            send(ANNOTATION_MESSAGE, {
                type: 'stroke',
                id: stroke.id,
                points: points.length > 1 ? points : [ points[0], points[0] ],
                brushType: 'pen',
                color,
                displayName: localName
            });
        };
        const onPointerDown = event => {
            const point = event.button === 0 && toFrame(event);

            if (!point) {
                return;
            }
            canvas.setPointerCapture(event.pointerId);
            stroke = {
                id: `${localId}-${++_strokeCount}`,
                points: [ point ],
                sent: 0
            };
            last = point;
            addSegment({
                sharerId,
                senderId: localId,
                points: [ point, point ],
                color
            });
            timer = setTimeout(flush, SEND_INTERVAL_MS);
        };
        const onPointerMove = event => {
            const point = toFrame(event);

            if (!point) {
                return;
            }
            if (!stroke) {
                // Hovering: show the sharer where we are about to draw.
                if (Date.now() - lastPointerAt >= SEND_INTERVAL_MS) {
                    lastPointerAt = Date.now();
                    send(POINTER_MESSAGE, {
                        type: 'pointer',
                        displayName: localName,
                        x: point[0],
                        y: point[1],
                        color
                    });
                }

                return;
            }
            if (Math.abs(point[0] - last[0]) + Math.abs(point[1] - last[1]) < 0.001) {
                return;
            }
            addSegment({
                sharerId,
                senderId: localId,
                points: [ last, point ],
                color
            });
            stroke.points.push(point);
            last = point;
            if (!timer) {
                timer = setTimeout(flush, SEND_INTERVAL_MS);
            }
        };
        const onPointerUp = () => {
            flush();
            stroke = undefined;
        };
        const onPointerLeave = () => {
            stroke || send(POINTER_MESSAGE, { type: 'pointer-gone' });
        };
        const onKeyDown = event => {
            event.key === 'Escape' && dispatch(setAnnotationDrawing(false));
        };

        canvas.addEventListener('pointerdown', onPointerDown);
        canvas.addEventListener('pointermove', onPointerMove);
        canvas.addEventListener('pointerup', onPointerUp);
        canvas.addEventListener('pointercancel', onPointerUp);
        canvas.addEventListener('pointerleave', onPointerLeave);
        keyWindow.addEventListener('keydown', onKeyDown);

        return () => {
            canvas.removeEventListener('pointerdown', onPointerDown);
            canvas.removeEventListener('pointermove', onPointerMove);
            canvas.removeEventListener('pointerup', onPointerUp);
            canvas.removeEventListener('pointercancel', onPointerUp);
            canvas.removeEventListener('pointerleave', onPointerLeave);
            keyWindow.removeEventListener('keydown', onKeyDown);
            flush();
            send(POINTER_MESSAGE, { type: 'pointer-gone' });
        };
    }, [ active, color, conference, localId, localName, sharerId ]);

    if (!sharerId) {
        return null;
    }

    return (
        <>
            <canvas
                aria-hidden = { true }
                ref = { canvasRef }
                style = { active ? _styles.drawingCanvas : _styles.canvas } />
            {canDraw && (
                <div style = { _styles.bar }>
                    <button
                        aria-pressed = { active }
                        onClick = { () => dispatch(setAnnotationDrawing(!active)) }
                        style = { active ? _styles.activeButton : _styles.button }
                        type = 'button'>
                        <span
                            style = {{
                                ..._styles.swatch,
                                background: color
                            }} />
                        {t(active ? 'screenAnnotation.stopDrawing' : 'screenAnnotation.draw')}
                    </button>
                </div>
            )}
            {isSharer && (
                <div style = { _styles.bar }>
                    <button
                        aria-pressed = { allowed }
                        onClick = { () => dispatch(setRemoteAnnotationAllowed(!allowed)) }
                        style = { allowed ? _styles.activeButton : _styles.button }
                        type = 'button'>
                        {t(allowed ? 'screenAnnotation.viewersCanDraw' : 'screenAnnotation.viewersCannotDraw')}
                    </button>
                    <button
                        onClick = { () => dispatch(clearRemoteAnnotations()) }
                        style = { _styles.button }
                        type = 'button'>
                        {t('screenAnnotation.clear')}
                    </button>
                    {!inPip && (

                        // A browser cannot draw over the sharer's other windows; this floating
                        // window of the meeting, showing the share, is the closest it gets.
                        <button
                            onClick = { () => dispatch(showLocalShareWithAnnotations()) }
                            style = { _styles.button }
                            type = 'button'>
                            {t('screenAnnotation.floatingView')}
                        </button>
                    )}
                </div>
            )}
        </>
    );
}
